import React, { useEffect, useState } from "react";
import { Search, Globe2, X } from "lucide-react";
import { FilterRow, FilterChip } from "./VisualFilter";
import { TIER_VALUES, TIER_LABELS } from "./TierBadge";
import { useApi } from "../api/useApi";
import { searchContacts } from "../api/resources";
import type { ContactSearchItem } from "../api/resources";

/**
 * Shared list-filter primitives (user direction 2026-08-27: "this filter component should
 * be generic and repeated across the platform all over the place. Same components").
 * Sessions and Rankings both compose these; both persist selections in the URL via the
 * parse/encode helpers below. All filters are multi-entry — values OR together.
 */

export type AthleteRef = { id: number; name: string };

/**
 * URL-persisted athlete selection: "4242:Rory Pro,2027:Tiger Woods". The name rides along
 * so a restored URL can render its chips without a lookup; commas/colons are stripped from
 * names on write because they are the encoding's delimiters.
 */
export function parseAthletesParam(raw: string | null): AthleteRef[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((entry) => {
      const sep = entry.indexOf(":");
      const id = Number(sep >= 0 ? entry.slice(0, sep) : entry);
      const name = sep >= 0 ? entry.slice(sep + 1) : "";
      return Number.isFinite(id) && id > 0 ? { id, name: name || `#${id}` } : null;
    })
    .filter((a): a is AthleteRef => a != null);
}

export function encodeAthletesParam(athletes: AthleteRef[]): string {
  return athletes.map((a) => `${a.id}:${a.name.replace(/[,:]/g, " ").trim()}`).join(",");
}

/** Multi-select tier chips: All + the five tiers. Viewing/filtering is ungated for
 * everyone (only ASSIGNING Elite Pro is admin-only). */
export function TierFilterRow({
  tiers, onToggle, onClear, hint = "pick several — filters on the server",
}: {
  tiers: string[];
  onToggle: (tier: string) => void;
  onClear: () => void;
  hint?: string;
}) {
  return (
    <FilterRow label="Athlete tier" hint={hint}>
      <FilterChip
        active={tiers.length === 0}
        onClick={onClear}
        icon={<Globe2 className="h-4 w-4" />}
        label="All tiers"
      />
      {TIER_VALUES.map((t) => (
        <FilterChip
          key={t}
          active={tiers.includes(t)}
          onClick={() => onToggle(t)}
          label={TIER_LABELS[t]}
        />
      ))}
    </FilterRow>
  );
}

/**
 * Multi-select athlete picker: debounced contactsSearch (the same search subject tagging
 * uses), search box on the LEFT with the selected pills accumulating to its right.
 */
export function AthleteFilterRow({
  athletes, onAdd, onRemove, label = "Athlete", hint = "pick several",
}: {
  athletes: AthleteRef[];
  onAdd: (a: AthleteRef) => void;
  onRemove: (id: number) => void;
  label?: string;
  hint?: string;
}) {
  const apiFetch = useApi();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<ContactSearchItem[] | null>(null);
  const [searching, setSearching] = useState(false);

  // Debounced search (300ms, same as the SessionDetail tag-athlete picker — a UI
  // responsiveness choice, not a derived threshold).
  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const resp = await searchContacts(apiFetch, term);
        if (!cancelled) setResults(resp?.values ?? []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [q, apiFetch]);

  const pick = (a: AthleteRef) => {
    setQ("");
    setResults(null);
    onAdd(a);
  };

  return (
    <FilterRow label={label} hint={hint}>
      <div className="relative w-full max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search athletes…"
          className="w-full h-9 pl-9 pr-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
        />
        {q.trim() && (
          <div className="absolute z-10 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg overflow-hidden">
            {searching ? (
              <div className="px-3 py-2 text-sm text-slate-500">Searching…</div>
            ) : (results ?? []).length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-500">No athletes found.</div>
            ) : (
              (results ?? []).map((r) => (
                <button
                  key={r.contactId}
                  type="button"
                  onClick={() => pick({ id: r.contactId, name: r.name })}
                  className="block w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-el-50"
                >
                  {r.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {athletes.map((a) => (
        <span
          key={a.id}
          className="inline-flex items-center gap-1 rounded-full border border-el-200 bg-el-50 pl-3 pr-1 py-1 text-sm text-el-700"
        >
          {a.name}
          <button
            type="button"
            aria-label={`Remove ${a.name}`}
            onClick={() => onRemove(a.id)}
            className="p-1 rounded-full hover:bg-el-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ))}
    </FilterRow>
  );
}
